import { useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  ChartType,
  ChartData,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import type { FinancialHealthStats } from '../../types/financialStats';
import { ChartOverlayWrapper } from './ChartOverlayWrapper';
import ChartExpandModal from './ChartExpandModal';
import ChartHeader from './ChartHeader';
import { useChartDrag, withDragHandlers } from './ChartDragUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController
);

export interface BaseChartProps<T = FinancialHealthStats> {
  data: T[];
  showDebug?: boolean;
  monthsToShow?: number;
  onExpandClick?: React.MutableRefObject<any>;
  title: string;
  dataKey: keyof T;
  color?: string;
  yAxisFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => string[];
  renderOverlay: (visibleData: T[]) => React.ReactNode;
  min?: number;
  max?: number;
  isCurrentFinances?: boolean;
  // Optional point styling to match VeraScoreChart
  pointRadius?: ((ctx: any) => number) | number;
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  pointBorderWidth?: number;
  borderWidth?: number;
  tension?: number;
  // Allow custom datasets for trend lines
  customDatasets?: any[];
  // Option to hide the default header
  hideHeader?: boolean;
  // Y-axis step size override
  yAxisStepSize?: number;
  // Chart height
  chartHeight?: string;
  // Custom message overlay for special states (like no late payments)
  customMessageOverlay?: ReactNode;
}

type BaseChartType = ChartJS<ChartType, number[], string>;

export const BaseChartTemplate = <T extends { month: string } = FinancialHealthStats>({
  data,
  showDebug,
  monthsToShow = 24, // Default to 24 months to match VeraScore chart
  onExpandClick,
  title,
  dataKey,
  color = '#1C4583',
  yAxisFormatter,
  tooltipFormatter,
  renderOverlay,
  min,
  max,
  isCurrentFinances = false,
  pointRadius,
  pointBackgroundColor,
  pointBorderColor,
  pointBorderWidth,
  borderWidth,
  tension,
  customDatasets,
  hideHeader = false,
  yAxisStepSize,
  chartHeight,
  customMessageOverlay,
}: BaseChartProps<T>) => {
  const chartRef = useRef<BaseChartType>(null);
  
  // Sort data chronologically to ensure consistent display across all views
  const sortedData = [...data].sort((a, b) => {
    return new Date(a.month).getTime() - new Date(b.month).getTime();
  });
  
  const [visibleData, setVisibleData] = useState<T[]>(
    sortedData.slice(-monthsToShow)
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Enhanced handleDataChange with animation management
  const handleDataChange = useCallback((newVisibleData: T[]) => {
    const chart = chartRef.current;
    if (chart) {
      // Store original animation settings
      const originalAnimation = chart.options.animation;
      
      // Disable animations temporarily
      chart.options.animation = false;
      
      // Update the chart data directly to avoid rendering flicker
      chart.data.labels = newVisibleData.map(d => d.month);
      chart.data.datasets[0].data = newVisibleData.map(d => Number(d[dataKey]) || 0);
      
      // Update with 'none' mode for instant change
      chart.update('none');
      
      // Update state after chart is updated
      setVisibleData(newVisibleData);
      
      // Re-enable animations after a short delay
      setTimeout(() => {
        if (chart) {
          chart.options.animation = originalAnimation;
        }
      }, 100);
    } else {
      setVisibleData(newVisibleData);
    }
  }, [dataKey]);

  // Use the chart drag hook for consistent behavior
  const {
    containerRef,
    isDragging,
    viewWindow,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    goEarlier,
    goLater
  } = useChartDrag(sortedData, monthsToShow, handleDataChange);

  // Get event handlers for drag functionality
  const dragHandlers = withDragHandlers({
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    isDragging
  });

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Skip custom gradient application if customDatasets is provided
    if (customDatasets) return;

    const ctx = chart.ctx;

    // Create gradient for the line
    const lineGradient = ctx.createLinearGradient(0, 0, 0, 400);
    lineGradient.addColorStop(0, `${color}E6`); // 90% opacity
    lineGradient.addColorStop(1, `${color}1A`); // 10% opacity

    // Create gradient for the area
    const areaGradient = ctx.createLinearGradient(0, 0, 0, 400);
    areaGradient.addColorStop(0, `${color}33`); // 20% opacity
    areaGradient.addColorStop(1, `${color}05`); // 2% opacity

    if (chart.data.datasets) {
      chart.data.datasets[0].backgroundColor = areaGradient;
      chart.data.datasets[0].borderColor = lineGradient;
    }

    chart.update();
  }, [visibleData, color, customDatasets]);
  
  // Update visible data when the input data changes
  useEffect(() => {
    // Re-sort the data and update visible data
    const newSortedData = [...data].sort((a, b) => {
      return new Date(a.month).getTime() - new Date(b.month).getTime();
    });
    setVisibleData(newSortedData.slice(-monthsToShow) as T[]);
  }, [data, monthsToShow]);

  useEffect(() => {
    if (onExpandClick) {
      const handleExpandClick = () => {
        setIsModalOpen(true);
      };
      // This is a bit of a hack, but it allows us to expose the function
      // @ts-ignore
      onExpandClick.current = onExpandClick.current || {};
      // @ts-ignore
      onExpandClick.current.openModal = handleExpandClick;
    }
  }, [onExpandClick]);

  const chartData: ChartData<'line', number[]> = {
    labels: visibleData.map(d => d.month),
    datasets: customDatasets || [
      {
        label: title,
        data: visibleData.map(d => Number(d[dataKey]) || 0),
        fill: true,
        tension: tension || 0.4,
        borderWidth: borderWidth || 1.5,
        pointHoverRadius: 4,
        pointBackgroundColor: pointBackgroundColor || color,
        pointBorderColor: pointBorderColor || '#fff',
        pointBorderWidth: pointBorderWidth || 1,
        spanGaps: true,
        borderCapStyle: 'round' as const,
        borderJoinStyle: 'round' as const,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 300 // Moderate animation speed (0.3 seconds)
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: pointRadius !== undefined ? pointRadius : 0,
        hitRadius: 10,
        hoverRadius: 4,
        hoverBorderWidth: 1
      }
    },
    transitions: {
      active: {
        animation: {
          duration: 150 // Slightly faster transitions
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const,
      axis: 'x' as const,
    },
    layout: {
      padding: {
        top: 10,
        right: 20,
        bottom: 10,
        left: 10
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Manrope', sans-serif",
          },
          color: '#64748B',
          maxRotation: 45,
          minRotation: 45,
          callback: function(_value: any, index: any) {
            try {
              const date = parseISO(visibleData[index].month);
              return format(date, 'MMM yyyy');
            } catch (e) {
              return visibleData[index]?.month || '';
            }
          }
        },
      },
      y: {
        position: 'left' as const,
        min: min,
        max: max,
        grid: {
          color: '#E2E8F0',
          drawTicks: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Manrope', sans-serif",
          },
          color: '#64748B',
          padding: 10,
          callback: function(tickValue: string | number) {
            if (yAxisFormatter) {
              return yAxisFormatter(Number(tickValue));
            }
            return String(tickValue);
          },
          // Use provided step size or calculate a reasonable one based on the data range
          stepSize: yAxisStepSize || (() => {
            // Special handling for currency charts
            if (dataKey === 'gross_income' || dataKey === 'money_in' || 
                dataKey === 'monthly_expenses' || dataKey === 'money_leftover' || 
                dataKey === 'net_savings' || dataKey === 'minimum_debt_payments') {
              return 1000; // Use 1000 as the step size for currency charts
            }
            
            // If no values exist, use 10 as a default
            if (visibleData.length === 0) return 10;
            
            // If min and max are provided, use those values
            if (min !== undefined && max !== undefined) {
              const range = max - min;
              // Use at most 10 ticks for the chart
              return Math.ceil(range / 10);
            }
            
            // Otherwise calculate from the data
            const values = visibleData.map(d => Number(d[dataKey as keyof typeof d] || 0)).filter(val => !isNaN(val));
            if (values.length === 0) return 10;
            
            const dataMin = Math.min(...values);
            const dataMax = Math.max(...values);
            const range = dataMax - dataMin;
            
            // Use at most 10 ticks for the chart
            return Math.max(1, Math.ceil(range / 10));
          })(),
        },
        border: {
          dash: [4, 4],
          display: false,
        },
      },
    },
    plugins: {
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1C4583',
        titleFont: {
          size: 14,
          weight: 600,
          family: "'Manrope', sans-serif",
        },
        bodyColor: '#64748B',
        bodyFont: {
          size: 13,
          family: "'Manrope', sans-serif",
        },
        padding: 12,
        borderColor: '#E2E8F0',
        borderWidth: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        cornerRadius: 8,
        callbacks: {
          title: (tooltipItems: any) => {
            try {
              if (!tooltipItems?.[0]?.label) return '';
              const dataIndex = tooltipItems[0].dataIndex;
              const date = parseISO(visibleData[dataIndex].month);
              return format(date, 'MMMM yyyy');
            } catch (e) {
              return tooltipItems?.[0]?.label || '';
            }
          },
          label: (context: any) => {
            const value = context.raw;
            const datasetIndex = context.datasetIndex;
            
            // Different labels based on whether it's the main data or trend line
            if (customDatasets && customDatasets.length > 1) {
              // If using custom datasets, check which dataset this is
              if (datasetIndex === 0) {
                // Main data line
                if (tooltipFormatter) {
                  const [formattedValue, label] = tooltipFormatter(value);
                  return [`Month: ${formattedValue}`, label];
                }
                return [`Month: ${value}`, title];
              } else if (datasetIndex === 1) {
                // Trend line
                if (typeof value === 'number') {
                  // Format number similarly to how the main data is formatted
                  if (yAxisFormatter) {
                    return [`Average: ${yAxisFormatter(value)}`, 'Trend'];
                  }
                  return [`Average: ${value.toLocaleString()}`, 'Trend'];
                }
                return [`Average: ${value}`, 'Trend'];
              }
            }
            
            // Default case for non-custom datasets
            return tooltipFormatter ? tooltipFormatter(value) : [`${title}: ${value}`];
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  return (
    <div className="relative h-full">
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && showDebug && (
        <div className="absolute top-0 right-0 bg-white/80 p-2 text-xs text-slate-500 z-10 rounded border">
          <div>Total months: {sortedData.length}</div>
          <div>Visible: {visibleData.length}</div>
          <div>Window: {viewWindow.start} - {viewWindow.end}</div>
        </div>
      )}
      
      {/* Chart header with months shown and navigation buttons - conditionally rendered */}
      {!hideHeader && (
        <ChartHeader
          viewWindow={viewWindow}
          totalMonths={sortedData.length}
          monthsToShow={monthsToShow}
          onEarlierClick={goEarlier}
          onLaterClick={goLater}
        />
      )}
      
      {!isCurrentFinances ? (
        <div className="p-4 bg-white rounded-lg h-[calc(100%-40px)]">
          <ChartOverlayWrapper 
            overlay={isCurrentFinances ? null : renderOverlay(visibleData)} 
            showStatsButton={false}
          >
            <Chart
              ref={chartRef}
              type="line"
              data={chartData}
              options={options}
            />
          </ChartOverlayWrapper>
        </div>
      ) : (
        <div 
          ref={containerRef}
          className="p-0 rounded-xl overflow-hidden w-full flex-grow"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(8px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.03), 0 1px 6px rgba(0, 0, 0, 0.02)',
            height: chartHeight || 'calc(100% - 40px)', 
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            ...(dragHandlers.style || {})
          }}
          onTouchStart={dragHandlers.onTouchStart}
          onTouchMove={dragHandlers.onTouchMove}
          onTouchEnd={dragHandlers.onTouchEnd}
          onMouseDown={dragHandlers.onMouseDown}
          onMouseMove={dragHandlers.onMouseMove}
          onMouseUp={dragHandlers.onMouseUp}
          onMouseLeave={dragHandlers.onMouseLeave}
        >
          <div className="relative h-full pb-6">
            <ChartOverlayWrapper 
              overlay={null} 
              showStatsButton={false}
            >
              <Chart
                ref={chartRef}
                type="line"
                data={chartData}
                options={options}
              />
              {/* Show custom message overlay if provided */}
              {customMessageOverlay && (
                <div className="absolute inset-0 z-10 pointer-events-none">
                  {customMessageOverlay}
                </div>
              )}
            </ChartOverlayWrapper>
          </div>
        </div>
      )}

      {/* Chart Expand Modal */}
      {isModalOpen && (
        <ChartExpandModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={data as FinancialHealthStats[]}
          title={title}
          dataKey={dataKey as keyof FinancialHealthStats}
          color={color}
          yAxisFormatter={yAxisFormatter}
          min={min}
          max={max}
          isCurrentFinances={isCurrentFinances}
          renderOverlay={renderOverlay as any}
          customMessageOverlay={customMessageOverlay}
        />
      )}
    </div>
  );
}; 